/**
 * Builds {@link AggregatedJobData} for SRP SEO from a single Algolia response
 * (hits + facets). Fills salary, employers (human-readable names), fields, and
 * sample-based language/remote/full-time percentages so meta descriptions are
 * data-rich on location and other filtered pages.
 */

import type { AggregatedJobData } from '../../types/srp-seo-meta';
import type { FacetsData } from '../facets/facets-shared';
import { extractFacetsFromSearchResults } from '../facets/facets-shared';
import { buildTopEmployersStripFallbackFromSearch } from '../srp/build-top-employers-strip-fallback-from-search';
import { fmtSalary } from './generate-srp-seo-meta';

const MIN_HITS_FOR_SAMPLE_PCTS = 3;

function getTopFacetLabels(
  facetsData: FacetsData,
  attribute: string,
  n: number,
  keep: (label: string) => boolean = () => true,
): string[] {
  const values = facetsData[attribute];
  if (!Array.isArray(values)) return [];
  return [...values]
    .sort((a: { count: number }, b: { count: number }) => b.count - a.count)
    .map((v: { label?: string; value: string }) => v.label || v.value)
    .filter((label): label is string => Boolean(label) && keep(label))
    .slice(0, n);
}

/** Sum of facet counts whose value (or label) matches, across the localized value sets. */
function sumFacetCount(facetsData: FacetsData, attribute: string, matches: (value: string) => boolean): number {
  const values = facetsData[attribute];
  if (!Array.isArray(values)) return 0;
  let total = 0;
  for (const v of values as Array<{ value: string; label?: string; count: number }>) {
    if (matches(v.value) || (v.label ? matches(v.label) : false)) total += v.count;
  }
  return total;
}

// Localized enum values from lambda/utils/processor/locale-enum-fields.ts, matched case-insensitively.
const SWEDISH_LANGUAGE_VALUES = new Set(['swedish', 'ruotsi', 'svenska']);
const NO_DEGREE_VALUES = new Set(['no degree required', 'ei koulutusta vaadita', 'ingen utbildning krävs']);
const SUMMER_JOB_VALUES = new Set(['summer job', 'kesätyö', 'sommarjobb']);
const inSet = (set: Set<string>) => (value: string) => set.has(value.trim().toLowerCase());

/**
 * The location facet mixes real towns with remote pseudo-locations ("Etätyö - Espanja",
 * "Remote - Europe", "Distans - Global") and bare country rows ("Suomi", "Finland"). A meta
 * description that says "kaupungit: Etätyö -…" reads as broken, so only real place names make
 * the "top cities" list.
 */
const PSEUDO_LOCATION_RE = /^(remote|etätyö|distans|hybrid)\b|\s-\s|^(suomi|finland|sverige|ruotsi)$/i;

function isRealCityLabel(label: string): boolean {
  return !PSEUDO_LOCATION_RE.test(label.trim());
}

function midSalaryFromHit(hit: Record<string, unknown>): number | null {
  const min = hit.salaryRangeMin;
  const max = hit.salaryRangeMax;
  if (typeof min !== 'number' || typeof max !== 'number' || min <= 0 || max <= 0) return null;
  return (min + max) / 2;
}

/**
 * Prefer Algolia facetStats (full result set); fall back to the current page of hits.
 */
function averageSalaryFromFacetStatsOrHits(
  facetStats: Record<string, { avg?: number; min?: number; max?: number }> | undefined,
  hits: Record<string, unknown>[],
): number | null {
  const avg = facetStats?.salaryRangeMin?.avg;
  if (typeof avg === 'number' && Number.isFinite(avg) && avg > 0) {
    return Math.round(avg);
  }
  const mids: number[] = [];
  for (const h of hits) {
    const m = midSalaryFromHit(h);
    if (m != null) mids.push(m);
  }
  if (mids.length === 0) return null;
  return Math.round(mids.reduce((a, b) => a + b, 0) / mids.length);
}

function salaryRangeLabelFromFacetStatsOrHits(
  facetStats: Record<string, { min?: number; max?: number }> | undefined,
  hits: Record<string, unknown>[],
): string | null {
  const fsMin = facetStats?.salaryRangeMin?.min;
  const fsMax = facetStats?.salaryRangeMax?.max ?? facetStats?.salaryRangeMin?.max;
  if (typeof fsMin === 'number' && typeof fsMax === 'number' && fsMin > 0 && fsMax > 0) {
    return `€${fmtSalary(fsMin)} – €${fmtSalary(fsMax)}`;
  }

  let minV = Infinity;
  let maxV = -Infinity;
  for (const h of hits) {
    const min = h.salaryRangeMin;
    const max = h.salaryRangeMax;
    if (typeof min === 'number' && typeof max === 'number' && min > 0 && max > 0) {
      minV = Math.min(minV, min);
      maxV = Math.max(maxV, max);
    }
  }
  if (!Number.isFinite(minV) || !Number.isFinite(maxV)) return null;
  return `€${fmtSalary(minV)} – €${fmtSalary(maxV)}`;
}

function hitMentionsEnglish(hit: Record<string, unknown>): boolean {
  const langs = hit.requiredLanguages;
  if (Array.isArray(langs)) {
    return langs.some((l) => {
      const s = String(l).toLowerCase();
      return s.includes('english') || s === 'en' || s.includes('englanti');
    });
  }
  if (typeof langs === 'string') {
    const s = langs.toLowerCase();
    return s.includes('english') || s.includes('englanti');
  }
  return false;
}

function hitIsRemoteOrHybrid(hit: Record<string, unknown>): boolean {
  const r = String(hit.remoteness ?? '').toLowerCase();
  return r.includes('remote') || r.includes('hybrid') || r.includes('etä') || r.includes('distans');
}

function hitIsFullTime(hit: Record<string, unknown>): boolean {
  const jt = String(hit.jobType ?? '').toLowerCase();
  return jt.includes('full') || jt.includes('kokoaika') || jt.includes('heltid');
}

function samplePercentages(hits: Record<string, unknown>[]): Pick<
  AggregatedJobData,
  'englishPct' | 'remotePct' | 'fullTimePct'
> {
  if (hits.length < MIN_HITS_FOR_SAMPLE_PCTS) {
    return { englishPct: null, remotePct: null, fullTimePct: null };
  }
  let englishWithLang = 0;
  let langDenom = 0;
  let remote = 0;
  let fullTime = 0;
  const n = hits.length;
  for (const h of hits) {
    const hasLang =
      h.requiredLanguages != null &&
      (Array.isArray(h.requiredLanguages)
        ? h.requiredLanguages.length > 0
        : String(h.requiredLanguages).length > 0);
    if (hasLang) {
      langDenom++;
      if (hitMentionsEnglish(h)) englishWithLang++;
    }
    if (hitIsRemoteOrHybrid(h)) remote++;
    if (hitIsFullTime(h)) fullTime++;
  }
  return {
    englishPct: langDenom > 0 ? Math.round((englishWithLang / langDenom) * 100) : null,
    remotePct: Math.round((remote / n) * 100),
    fullTimePct: Math.round((fullTime / n) * 100),
  };
}

export function buildAggregatedJobDataFromSearchResults(
  searchResults: unknown,
  currentFacetFilters: Record<string, string[]> = {},
  locale: 'en' | 'fi' | 'sv' = 'en',
): Omit<AggregatedJobData, 'count'> {
  const sr = searchResults as {
    hits?: Record<string, unknown>[];
    facets?: Record<string, Record<string, number>>;
    facetStats?: Record<string, { min?: number; max?: number; avg?: number }>;
  } | null;

  if (!sr) {
    return {
      avgSalary: null,
      salaryMin: null,
      salaryMax: null,
      topEmployers: [],
      topCities: [],
      topFields: [],
      topRoles: [],
      swedishPct: null,
      noDegreeCount: null,
      summerJobCount: null,
      topSkills: [],
      topBenefits: [],
      englishPct: null,
      remotePct: null,
      fullTimePct: null,
      salaryRangeLabel: null,
    };
  }

  const hits = Array.isArray(sr.hits) ? sr.hits : [];
  const facetsData = extractFacetsFromSearchResults(sr as any, currentFacetFilters, locale);

  const strip = buildTopEmployersStripFallbackFromSearch(facetsData, hits, 8);
  const topEmployers = strip.map((s) => s.name).filter((n) => n && n.trim()).slice(0, 3);

  const facetStats = sr.facetStats;

  const avgSalary = averageSalaryFromFacetStatsOrHits(facetStats, hits);
  const salaryRangeLabel = salaryRangeLabelFromFacetStatsOrHits(facetStats, hits);

  const pcts = samplePercentages(hits);

  return {
    avgSalary,
    salaryMin: typeof facetStats?.salaryRangeMin?.min === 'number' ? facetStats.salaryRangeMin.min : null,
    salaryMax: typeof facetStats?.salaryRangeMax?.max === 'number' ? facetStats.salaryRangeMax.max : null,
    topEmployers,
    topCities: getTopFacetLabels(facetsData, 'location', 3, isRealCityLabel),
    topFields: getTopFacetLabels(facetsData, 'companyIndustry', 3),
    topRoles: getTopFacetLabels(facetsData, 'normalisedTitle', 3),
    swedishPct: (() => {
      const total = typeof sr.nbHits === 'number' ? sr.nbHits : hits.length;
      const swedish = sumFacetCount(facetsData, 'requiredLanguages', inSet(SWEDISH_LANGUAGE_VALUES));
      return total > 0 && swedish > 0 ? Math.round((swedish / total) * 100) : null;
    })(),
    noDegreeCount: sumFacetCount(facetsData, 'educationLevel', inSet(NO_DEGREE_VALUES)) || null,
    summerJobCount: sumFacetCount(facetsData, 'jobType', inSet(SUMMER_JOB_VALUES)) || null,
    topSkills: getTopFacetLabels(facetsData, 'skillsAndTechnologies', 3),
    topBenefits: getTopFacetLabels(facetsData, 'benefits', 3),
    englishPct: pcts.englishPct,
    remotePct: pcts.remotePct,
    fullTimePct: pcts.fullTimePct,
    salaryRangeLabel,
  };
}
